import React, { useEffect, useState } from 'react'
import { Alert, Badge, Card, Col, Container, Nav, Row, Spinner, Tab, Table } from 'react-bootstrap'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { EMPLOYMENT_TYPES, STAFF_STATUS, UPLOAD_BASE, fmtDateTH } from './hrShared'

const InfoItem = ({ label, value }) => (
  <Col xs={12} md={4} className="mb-3">
    <div className="text-muted fs-sm">{label}</div>
    <div>{value || '-'}</div>
  </Col>
)

export default function MyHrProfile() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    axiosInstance.get('/api/hr/me/profile')
      .then(res => { if (res.data.success) setData(res.data.data) })
      .catch(err => setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ'))
      .finally(() => setLoading(false))
  }, [])

  const st = STAFF_STATUS[data?.hp_status] || STAFF_STATUS.active

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h4 className="main-title mb-0">
              <i className="ri-profile-line me-2 text-primary" />
              ประวัติของฉัน
            </h4>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : data && (
            <>
              <Card className="card-one mb-3">
                <Card.Body className="d-flex flex-wrap align-items-center gap-3">
                  <div>
                    <h5 className="mb-1">{data.t_name}</h5>
                    <div className="text-muted fs-sm">
                      รหัสประจำตัว <code>{data.t_idnumber}</code>
                      {data.t_phone && <> · โทร {data.t_phone}</>}
                    </div>
                    <div className="mt-1">
                      {(data.positions || []).map(p => (
                        <Badge bg="light" text="dark" className="me-1 border" key={p.pos_id}>{p.pos_name}</Badge>
                      ))}
                    </div>
                  </div>
                  <div className="ms-auto text-end">
                    <Badge bg={st.bg} className="fs-sm">{st.label}</Badge>
                    {data.hp_department && <div className="text-muted fs-sm mt-1">{data.hp_department}</div>}
                  </div>
                </Card.Body>
              </Card>

              <Tab.Container defaultActiveKey="profile">
                <Card className="card-one">
                  <Card.Header>
                    <Nav variant="tabs" className="card-header-tabs">
                      <Nav.Item><Nav.Link eventKey="profile">ข้อมูลส่วนตัว</Nav.Link></Nav.Item>
                      <Nav.Item><Nav.Link eventKey="education">การศึกษา</Nav.Link></Nav.Item>
                      <Nav.Item><Nav.Link eventKey="work">ประวัติการทำงาน</Nav.Link></Nav.Item>
                      <Nav.Item><Nav.Link eventKey="documents">เอกสาร</Nav.Link></Nav.Item>
                    </Nav>
                  </Card.Header>
                  <Card.Body>
                    <Tab.Content>
                      <Tab.Pane eventKey="profile">
                        <Row>
                          <InfoItem label="ตำแหน่ง" value={data.hp_position_title} />
                          <InfoItem label="ฝ่าย/แผนก" value={data.hp_department} />
                          <InfoItem label="ประเภทการจ้าง" value={EMPLOYMENT_TYPES[data.hp_employment_type]} />
                          <InfoItem label="วันที่เริ่มงาน" value={fmtDateTH(data.hp_hire_date)} />
                          <InfoItem label="วุฒิการศึกษาสูงสุด" value={data.hp_education_level} />
                          <InfoItem label="เบอร์โทร" value={data.t_phone} />
                          <InfoItem label="ที่อยู่" value={data.hp_address} />
                          <InfoItem label="ผู้ติดต่อฉุกเฉิน"
                            value={data.hp_emergency_name
                              ? `${data.hp_emergency_name}${data.hp_emergency_relation ? ` (${data.hp_emergency_relation})` : ''}${data.hp_emergency_phone ? ` · ${data.hp_emergency_phone}` : ''}`
                              : ''} />
                        </Row>
                        <div className="text-muted fs-sm mt-2">
                          หากข้อมูลไม่ถูกต้อง กรุณาติดต่องานบุคลากรเพื่อขอแก้ไข
                        </div>
                      </Tab.Pane>

                      <Tab.Pane eventKey="education">
                        <Table responsive hover className="mb-0 fs-sm">
                          <thead className="table-light">
                            <tr><th>ระดับ</th><th>วุฒิ/ปริญญา</th><th>สาขา</th><th>สถาบัน</th><th>ปีที่จบ (พ.ศ.)</th></tr>
                          </thead>
                          <tbody>
                            {(data.education || []).length === 0 ? (
                              <tr><td colSpan={5} className="text-center text-muted py-4">ยังไม่มีข้อมูลการศึกษา</td></tr>
                            ) : data.education.map(ed => (
                              <tr key={ed.he_id}>
                                <td>{ed.he_level || '-'}</td>
                                <td>{ed.he_degree || '-'}</td>
                                <td>{ed.he_major || '-'}</td>
                                <td>{ed.he_institution || '-'}</td>
                                <td>{ed.he_grad_year || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Tab.Pane>

                      <Tab.Pane eventKey="work">
                        <Table responsive hover className="mb-0 fs-sm">
                          <thead className="table-light">
                            <tr><th>ตำแหน่ง</th><th>หน่วยงาน</th><th>เริ่ม</th><th>สิ้นสุด</th><th>หมายเหตุ</th></tr>
                          </thead>
                          <tbody>
                            {(data.workHistory || []).length === 0 ? (
                              <tr><td colSpan={5} className="text-center text-muted py-4">ยังไม่มีประวัติการทำงาน</td></tr>
                            ) : data.workHistory.map(w => (
                              <tr key={w.hw_id}>
                                <td>{w.hw_position || '-'}</td>
                                <td>{w.hw_organization || '-'}</td>
                                <td>{fmtDateTH(w.hw_start_date)}</td>
                                <td>{w.hw_end_date ? fmtDateTH(w.hw_end_date) : 'ปัจจุบัน'}</td>
                                <td>{w.hw_note || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Tab.Pane>

                      <Tab.Pane eventKey="documents">
                        <Table responsive hover className="mb-0 fs-sm">
                          <thead className="table-light">
                            <tr><th>ประเภท</th><th>ชื่อเอกสาร</th><th>อัปโหลดเมื่อ</th><th style={{ width: 60 }}></th></tr>
                          </thead>
                          <tbody>
                            {(data.documents || []).length === 0 ? (
                              <tr><td colSpan={4} className="text-center text-muted py-4">ยังไม่มีเอกสาร</td></tr>
                            ) : data.documents.map(d => (
                              <tr key={d.hd_id}>
                                <td>{d.hd_type || '-'}</td>
                                <td>{d.hd_title}</td>
                                <td>{fmtDateTH(d.created_at)}</td>
                                <td className="text-end">
                                  <a href={`${UPLOAD_BASE}/uploads/hr_documents/${d.hd_filename}`} target="_blank" rel="noreferrer" title="เปิดไฟล์">
                                    <i className="ri-download-2-line" />
                                  </a>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Tab.Pane>
                    </Tab.Content>
                  </Card.Body>
                </Card>
              </Tab.Container>
            </>
          )}
        </Container>
      </div>
      <Footer />
    </>
  )
}
